<template>
  <div class="import_config_wrapper">
    <div class="mgx_action_btn" @click="triggerFileInput">
      <div class="icon-wrapper">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 3V16M12 16L16 12M12 16L8 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M20 17V19C20 20.1046 19.1046 21 18 21H6C4.89543 21 4 20.1046 4 19V17" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
      <span>{{ $t('import_config') }}</span>
    </div>
    <input id="fileInput" type="file" accept="*" class="input_file" @change="importConfig" />

    <!-- 替换配置选择弹窗 -->
    <Teleport to="body">
      <div v-if="showReplaceDialog" class="replace_dialog_overlay" @click.self="cancelReplace">
        <div class="replace_dialog">
          <div class="replace_dialog_header">
            <span class="replace_dialog_title">{{ $t('import_config_replace_title') }}</span>
            <div class="mgx_close_btn" @click="cancelReplace">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
          </div>
          <p class="replace_dialog_desc">{{ $t('import_config_replace_desc') }}</p>
          <ul class="replace_profile_list">
            <li v-for="item in replaceOptions" :key="item.index" class="replace_profile_item" :class="{ selected: selectedReplaceIndex === item.index }" @click="selectedReplaceIndex = item.index">
              <div class="profile_info">
                <span class="profile_name">{{ item.name }}</span>
                <span v-if="item.slotIndex !== null" class="profile_slot">Slot {{ item.slotIndex + 1 }}</span>
              </div>
              <div class="selection_indicator"></div>
            </li>
          </ul>
          <div class="replace_dialog_actions">
            <div class="mgx_action_btn" @click="cancelReplace">
              <span>{{ $t('cancel') }}</span>
            </div>
            <div class="mgx_action_btn primary" :class="{ disabled: selectedReplaceIndex === null }" @click="confirmReplace">
              <span>{{ $t('confirm') }}</span>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { configSet } from '@/service/driver/config_set'
import { DialogType, enableDialog } from '../cdialog/dialog'
import i18n from '@/languages/i18n'
import { DeviceManager } from '@/service/device'
import { speciaBehiver, specialCheck, specialCheckCurrent } from '@/define/limit'
import { travelConfig } from '@/define/magnet_travel'
import { DeviceCapability } from '@/service/device_capability'
import { DataProfile } from '@hid/snapshot/profile'
import { MagnetSeniorType } from '@libs/types/enum'
import { DataTravel } from '@libs/types/response'
import { KK, SetMagnetSenior } from '@hid/define/define'
import { importConfigTravelClampValue } from '@/service/driver/travel_converter'
import { ref } from 'vue'
let { t } = i18n.global

// ─── 替换弹窗状态 ────────────────────────────────────────────────────────────
interface ReplaceOption {
  index: number
  name: string
  slotIndex: number | null
}
const showReplaceDialog = ref(false)
const replaceOptions = ref<ReplaceOption[]>([])
const selectedReplaceIndex = ref<number | null>(null)
const pendingImport = ref<{ name: string; content: string } | null>(null)

const openReplaceDialog = (name: string, content: string) => {
  const currentIndex = configSet.currentConfig.value
  replaceOptions.value = configSet.configList.value.map((item, index) => ({ index, name: item.Name, slotIndex: configSet.getConfigSlotIndex(item) })).filter((item) => item.index !== currentIndex)
  selectedReplaceIndex.value = replaceOptions.value[0]?.index ?? null
  pendingImport.value = { name, content }
  showReplaceDialog.value = true
}

const cancelReplace = () => {
  showReplaceDialog.value = false
  pendingImport.value = null
  selectedReplaceIndex.value = null
}

const confirmReplace = async () => {
  if (selectedReplaceIndex.value === null || !pendingImport.value) return
  showReplaceDialog.value = false
  try {
    await configSet.importReplace(pendingImport.value.name, pendingImport.value.content, selectedReplaceIndex.value)
  } catch (e) {
    console.error('[importReplace] failed:', e)
    enableDialog(t('setting_failed'), DialogType.notice, false)
  } finally {
    pendingImport.value = null
    selectedReplaceIndex.value = null
  }
}

const triggerFileInput = () => {
  const fileInput = document.getElementById('fileInput') as HTMLInputElement
  if (fileInput) {
    fileInput.click()
  }
}
// 同厂商的设备配置文件可以互相导入
const importConfig = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    const file = target.files[0]
    const reader = new FileReader()
    reader.onload = async (e) => {
      const result = e.target?.result
      if (typeof result === 'string') {
        const data: { name: string; id: number; content: string } = JSON.parse(result)
        if (!checkId(data.id)) {
          enableDialog(t('file_id_error'), DialogType.notice, false)
        } else {
          const content = await processImportedData(data.content)
          if (configSet.isOnboardConfigSupported && configSet.configList.value.length >= configSet.maxConfigCount) {
            openReplaceDialog(data.name, content)
          } else {
            configSet.setConfigPreset(data.name, content)
          }
        }
        target.value = ''
      }
    }
    reader.onerror = () => {
      enableDialog(t('file_read_error'), DialogType.notice, false, 2000)
    }
    reader.readAsText(file, 'UTF-8')
  }
}

// 相同设备商的设备支持互相导入
const checkId = (importID: number): boolean => {
  if (
    importID == DeviceManager.instance.state.currentPid ||
    (specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V2) && specialCheck(speciaBehiver.DevicesOfJingtai_V2, importID)) ||
    (specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V1) && specialCheck(speciaBehiver.DevicesOfJingtai_V1, importID)) ||
    (specialCheckCurrent(speciaBehiver.DeviceOfRoyuan) && specialCheck(speciaBehiver.DeviceOfRoyuan, importID)) ||
    (specialCheckCurrent(speciaBehiver.DevicesOfWitmod) && specialCheck(speciaBehiver.DevicesOfWitmod, importID)) ||
    (specialCheckCurrent(speciaBehiver.DeviceHe3SE) && specialCheck(speciaBehiver.DeviceHe3SE, importID)) ||
    (specialCheckCurrent(speciaBehiver.DevicesOfHuoChaiRen) && specialCheck(speciaBehiver.DevicesOfHuoChaiRen, importID))
  ) {
    return true
  }
  return false
}

const processImportedData = async (encodedData: string): Promise<string> => {
  const decodedString = atob(encodedData)
  const parsedProfile: DataProfile = JSON.parse(decodedString)

  // 数据处理
  const clampedProfile = clampMagnetTravelValues(parsedProfile)
  const checkedProfile = await checkFeatureSupport(clampedProfile)

  // 编码返回
  return btoa(JSON.stringify(checkedProfile))
}

// 判断设备是否支持KME, RS， 音律律动等功能
// KME 、RS 功能不支持，则不设置
// 音律律动功能不支持，则改成默认灯效
const checkFeatureSupport = async (originalData: DataProfile): Promise<DataProfile> => {
  const processedProfile: DataProfile = JSON.parse(JSON.stringify(originalData))
  if (processedProfile.ColorsDynamic.Run) {
    const isMusicRhythmAvailable = await DeviceCapability.isMusicRhythmAvailable()
    if (!isMusicRhythmAvailable) {
      processedProfile.ColorsDynamic.Run = false
      processedProfile.LightMode.Mode = 1
    }
  }
  // 并行检测设备能力
  const [isRS, isKME] = await Promise.all([DeviceCapability.isRSSupported(), DeviceCapability.isSupportKME()])
  const safeMap: Map<KK, SetMagnetSenior> = processedProfile.MagnetData ? new Map(Object.entries(processedProfile.MagnetData)) : new Map()

  const filteredMap = new Map<number, SetMagnetSenior>()
  for (const [key, item] of safeMap) {
    const isSupportRS = item.KeyType === MagnetSeniorType.SeniorKeyTypeRS
    const isSupportKME = item.KeyType === MagnetSeniorType.SeniorKeyTypeKME
    if (isSupportRS || isSupportKME) {
      if (isRS || isKME) {
        filteredMap.set(key, item)
      }
    } else {
      filteredMap.set(key, item)
    }
  }
  processedProfile.MagnetData = filteredMap
  return processedProfile
}

// 睿连维厂商的设备的行程范围不一致,在导入时需要做特殊处理下
const clampMagnetTravelValues = (profile: DataProfile): DataProfile => {
  const deviceConfig = travelConfig(DeviceManager.instance.state.currentPid)
  const { maxTravel, step } = deviceConfig
  const minValue = Math.floor(maxTravel / step) // 明确取整逻辑

  // 处理行程数据
  if (profile.MagnetTravel) {
    const travelMap = new Map(Object.entries(profile.MagnetTravel))
    const clampedMap = new Map<KK, DataTravel>()

    for (const [key, data] of travelMap) {
      const clampedData: DataTravel = {
        ...data,
        PressNum: clampValue(data.PressNum, minValue, maxTravel),
        ReleaseNum: clampValue(data.ReleaseNum, minValue, maxTravel),
        QuickTriggerPressNum: data.QuickTriggerPressNum !== 0 ? clampValue(data.QuickTriggerPressNum, minValue, maxTravel) : 0,
        QuickTriggerReleaseNum: data.QuickTriggerReleaseNum !== 0 ? clampValue(data.QuickTriggerReleaseNum, minValue, maxTravel) : 0,
      }

      clampedMap.set(Number(key) as KK, clampedData) // 创建新对象避免副作用
    }

    profile.MagnetTravel = clampedMap
  }

  return profile
}
// 通用值域限制方法
const clampValue = (value: number, min: number, max: number): number => {
  return importConfigTravelClampValue(value, min, max)
}

defineExpose({ openReplaceDialog })
</script>

<style scoped lang="less">
.input_file {
  display: none;
}

.replace_dialog_overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--glass-overlay-bg, rgba(0, 0, 0, 0.05));
  backdrop-filter: var(--glass-overlay-blur, blur(4px));
}

.replace_dialog {
  background: var(--bg-card, rgba(255, 255, 255, 0.9));
  backdrop-filter: var(--glass-surface-blur, blur(20px));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-l);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  padding: 32px;
  min-width: 400px;
  max-width: 520px;
  width: 90vw;
  animation: feedbackFadeIn 0.4s var(--transition-base);

  .replace_dialog_header {
    margin-bottom: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .replace_dialog_title {
    font-size: 18px;
    font-weight: 700;
    color: var(--color-dark, #222);
  }

  .replace_dialog_desc {
    font-size: 14px;
    color: var(--color-text-hint, #6a7282);
    margin: 0 0 24px;
    line-height: 1.6;
  }

  .replace_profile_list {
    list-style: none;
    margin: 0 0 28px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 320px;
    overflow-y: auto;
    padding-right: 4px;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: transparent !important;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: var(--radius-pill);
      background: rgba(0, 0, 0, 0.1);
    }
  }

  .replace_profile_item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-radius: var(--radius-m);
    border: 1.5px solid transparent;
    cursor: pointer;
    transition: var(--transition-base);
    background: rgba(0, 0, 0, 0.03);
    color: var(--color-dark, #222);

    &:hover {
      background: rgba(0, 0, 0, 0.05);
      transform: translateY(-2px);
    }

    &.selected {
      border-color: var(--brand-color-pure, #7c6fff);
      background: rgba(var(--brand-color-rgb, 124, 111, 255), 0.08);

      .selection_indicator {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--brand-color-pure);
        box-shadow: 0 0 10px rgba(var(--brand-color-rgb), 0.4);
      }
    }

    .profile_info {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .profile_name {
      font-size: 15px;
      font-weight: 600;
    }

    .profile_slot {
      font-size: 12px;
      color: var(--color-text-hint, #6a7282);
      opacity: 0.8;
    }
  }

  .replace_dialog_actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;

    .mgx_action_btn {
      min-width: 100px;
      justify-content: center;

      &.disabled {
        opacity: 0.4;
        cursor: not-allowed;
        pointer-events: none;
      }
    }
  }
}

@keyframes feedbackFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
</style>
